import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabaseClient'
import { useAuth } from '../context/AuthContext'

const statusOptions = [
  { value: 'achieved', label: 'حقق الهدف', color: '#bbf7d0', border: '#4ade80' },
  { value: 'needs_support', label: 'يحتاج دعم', color: '#fef9c3', border: '#facc15' },
  { value: 'not_achieved', label: 'لم يحقق', color: '#fee2e2', border: '#fca5a5' },
]

function statusLabel(value) {
  const found = statusOptions.find(s => s.value === value)
  return found ? found.label : '—'
}

function statusChipStyle(value) {
  const option = statusOptions.find(s => s.value === value)
  const bg = option?.color || '#e5e7eb'
  const border = option?.border || '#d1d5db'
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px 8px',
    borderRadius: 999,
    background: bg,
    border: `1px solid ${border}`,
    fontSize: 11,
    color: '#111827',
    minWidth: 72,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  }
}

function levelLabel(level) {
  if (level === 'prekg') return 'مرحلة الحضانة'
  if (level === 'kg1') return 'صف الروضة (الأول)'
  if (level === 'kg2') return 'صف التمهيدي (الثاني)'
  return level || '—'
}

export default function Assessment() {
  const { user, signOut } = useAuth()
  const email = user?.email || ''
  const navigate = useNavigate()

  const today = useMemo(() => new Date(), [])
  const defaultTo = useMemo(() => today.toISOString().slice(0, 10), [today])
  const defaultFrom = useMemo(() => {
    const d = new Date(today)
    d.setMonth(d.getMonth() - 2)
    return d.toISOString().slice(0, 10)
  }, [today])

  const [fromDate, setFromDate] = useState(defaultFrom)
  const [toDate, setToDate] = useState(defaultTo)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [rows, setRows] = useState([])

  useEffect(() => {
    if (!fromDate || !toDate) return
    let active = true
    ;(async () => {
      setLoading(true)
      setError('')
      try {
        const { data, error: rpcError } = await supabase.rpc('get_kg_kid_assessment_summary', {
          p_from: fromDate,
          p_to: toDate,
        })
        if (!active) return
        if (rpcError) {
          setError(rpcError.message || 'حدث خطأ أثناء تحميل بيانات التقييم')
          setRows([])
        } else {
          setRows(data || [])
        }
      } catch (e) {
        if (!active) return
        setError(e.message || 'تعذر تحميل بيانات التقييم')
        setRows([])
      } finally {
        if (!active) return
        setLoading(false)
      }
    })()
    return () => { active = false }
  }, [fromDate, toDate])

  const summary = useMemo(() => {
    const counts = { achieved: 0, needs_support: 0, not_achieved: 0 }
    rows.forEach(r => {
      if (r.status && counts[r.status] != null) counts[r.status] += 1
    })
    return counts
  }, [rows])

  const groupedByUnit = useMemo(() => {
    const groups = {}
    rows.forEach(r => {
      const key = r.unit_name || 'أهداف أخرى'
      if (!groups[key]) groups[key] = []
      groups[key].push(r)
    })
    return groups
  }, [rows])

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: '1.5rem',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 10px 25px rgba(15,23,42,0.08)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0 }}>سجل التقييم والتطور - رياض الأطفال</h1>
            <button
              type="button"
              onClick={signOut}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              تسجيل الخروج
            </button>
          </div>

          <div style={{ color: '#6b7280', marginTop: 4, marginBottom: 12, fontSize: 14 }}>
            عرض آخر تقييمات الطفل للأهداف السلوكية ضمن وحدات الخبرة في رياض الأطفال.
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => navigate('/')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                لوحة المعلومات
              </button>
              <button
                type="button"
                onClick={() => navigate('/attendance')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                الحضور
              </button>
              <button
                type="button"
                onClick={() => navigate('/fees')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                الرسوم
              </button>
              <button
                type="button"
                onClick={() => navigate('/daily-plan')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                خطة اليوم
              </button>
              <button
                type="button"
                disabled
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'default', fontSize: 13 }}
              >
                التقييم
              </button>
            </div>
          </div>

          {email && (
            <p style={{ marginBottom: 8 }}>تم تسجيل الدخول بالبريد: <strong>{email}</strong></p>
          )}

          <div
            style={{
              marginBottom: 12,
              padding: 12,
              borderRadius: 12,
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              fontSize: 14,
            }}
          >
            <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label style={{ fontSize: 13, color: '#374151' }}>
                من تاريخ
                <input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  style={{
                    marginInlineStart: 8,
                    padding: '6px 8px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                  }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                إلى تاريخ
                <input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  style={{
                    marginInlineStart: 8,
                    padding: '6px 8px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                  }}
                />
              </label>
            </div>
            <div style={{ fontSize: 13, color: '#4b5563' }}>
              سيتم عرض آخر تقييم مسجل لكل هدف سلوكي ضمن هذه الفترة.
            </div>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>جاري تحميل بيانات التقييم...</div>
          )}

          {!loading && error && (
            <div style={{ color: '#b91c1c', marginTop: 16 }}>{error}</div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div style={{ marginTop: 16, fontSize: 14, color: '#6b7280' }}>
              لا توجد بيانات تقييم مسجلة في الفترة المحددة حتى الآن.
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <>
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 12,
                  padding: 12,
                  borderRadius: 12,
                  background: '#eff6ff',
                  border: '1px solid #dbeafe',
                  fontSize: 13,
                }}
              >
                <span style={{ marginInlineEnd: 12 }}>
                  عدد الأهداف التي تم تقييمها: <strong>{rows.length}</strong>
                </span>
                <span style={{ marginInlineEnd: 8 }}>
                  حقق الهدف: <strong>{summary.achieved}</strong>
                </span>
                <span style={{ marginInlineEnd: 8 }}>
                  يحتاج دعم: <strong>{summary.needs_support}</strong>
                </span>
                <span>
                  لم يحقق: <strong>{summary.not_achieved}</strong>
                </span>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                {Object.entries(groupedByUnit).map(([unitName, unitRows]) => (
                  <div
                    key={unitName}
                    style={{
                      borderRadius: 12,
                      border: '1px solid #e5e7eb',
                      background: '#fff',
                      padding: 12,
                    }}
                  >
                    <h3 style={{ margin: '0 0 6px 0', fontSize: 15 }}>
                      {unitName}
                    </h3>
                    {unitRows[0]?.unit_level && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                        المرحلة: {levelLabel(unitRows[0].unit_level)}
                      </div>
                    )}
                    <div style={{ display: 'grid', gap: 8 }}>
                      {unitRows.map(row => (
                        <div
                          key={row.objective_id}
                          style={{
                            borderRadius: 10,
                            border: '1px solid #e5e7eb',
                            padding: 10,
                            background: '#f9fafb',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>
                                {row.objective_description}
                              </div>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>
                                {row.objective_code && (
                                  <span style={{ marginInlineEnd: 8 }}>الكود: {row.objective_code}</span>
                                )}
                                {row.kg_level && (
                                  <span>مستوى المنهج: {levelLabel(row.kg_level)}</span>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                              <span style={statusChipStyle(row.status)}>{statusLabel(row.status)}</span>
                              {row.assessed_on && (
                                <span style={{ fontSize: 11, color: '#6b7280' }}>
                                  آخر تقييم: {String(row.assessed_on)}
                                </span>
                              )}
                            </div>
                          </div>
                          {row.comment && (
                            <div style={{ marginTop: 6, fontSize: 13, color: '#4b5563' }}>
                              ملاحظة المعلمة: {row.comment}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
